import { useCallback, useEffect, useMemo, useState } from 'react';

import routesPath from '@/shared/constants/routesPath';
import {
  clearSession as clearSessionStorage,
  getSession,
  saveSession,
} from '@/shared/store/session';
import useUserStore from '@/shared/store/user';

import {
  ensureValidToken,
  offSessionExpired,
  offSessionRefresh,
  onSessionExpired,
  onSessionRefresh,
} from '@/services/tokenRefreshService';

import { AuthContext } from './AuthContext';

const AuthProvider = ({ children }) => {
  const { saveUser, logout: logoutUserStore } = useUserStore();
  const [session, setSession] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedSession = getSession();

        if (!storedSession) {
          setIsAuthenticated(false);
          setSession(null);
          return;
        }

        setIsRefreshing(true);
        try {
          const validSession = await ensureValidToken();
          setSession(validSession);
          setIsAuthenticated(true);
          if (validSession.user) {
            saveUser(validSession.user);
          }
        } catch (error) {
          console.error('Error ensuring valid token on init:', error);
          clearSessionStorage();
          logoutUserStore();
          setSession(null);
          setIsAuthenticated(false);
          if (window.location.pathname !== routesPath.LOGIN) {
            window.location.href = routesPath.LOGIN;
          }
        } finally {
          setIsRefreshing(false);
        }
      } catch (error) {
        console.error('Error initializing auth:', error);
        setSession(null);
        setIsAuthenticated(false);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, [logoutUserStore, saveUser]);

  // Handle session refresh/expire callbacks
  useEffect(() => {
    const handleRefresh = (updatedSession) => {
      setSession(updatedSession);
      setIsAuthenticated(true);
      if (updatedSession.user) {
        saveUser(updatedSession.user);
      }
      setIsRefreshing(false);
    };

    const handleExpired = () => {
      clearSessionStorage();
      logoutUserStore();
      setSession(null);
      setIsAuthenticated(false);
      setIsRefreshing(false);
      if (window.location.pathname !== routesPath.LOGIN) {
        window.location.href = routesPath.LOGIN;
      }
    };

    onSessionRefresh(handleRefresh);
    onSessionExpired(handleExpired);

    return () => {
      offSessionRefresh(handleRefresh);
      offSessionExpired(handleExpired);
    };
  }, [logoutUserStore, saveUser]);

  // Cross-tab synchronization via storage events
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'session') {
        if (e.newValue) {
          try {
            const updatedSession = JSON.parse(e.newValue);
            setSession(updatedSession);
            setIsAuthenticated(true);
            if (updatedSession.user) {
              saveUser(updatedSession.user);
            }
          } catch (error) {
            console.error('Error parsing session from storage event:', error);
          }
        } else {
          setSession(null);
          setIsAuthenticated(false);
          logoutUserStore();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [logoutUserStore, saveUser]);

  // Login handler
  const onLogin = useCallback(
    async (sessionData) => {
      const loginTime = Date.now();
      const expiresIn = sessionData.expires_in || 3600; // default to 1 hour
      const expire = loginTime + expiresIn * 1000;

      const fullSession = {
        access_token: sessionData.access_token,
        refresh_token: sessionData.refresh_token,
        token_type: sessionData.token_type || 'Bearer',
        expire,
        loginTime,
        user: sessionData.user,
      };

      saveSession(fullSession);
      setSession(fullSession);
      setIsAuthenticated(true);
      if (fullSession.user) {
        saveUser(fullSession.user);
      }
    },
    [saveUser]
  );

  // Logout handler
  const onLogout = useCallback(() => {
    clearSessionStorage();
    logoutUserStore();
    setSession(null);
    setIsAuthenticated(false);
    window.location.href = routesPath.LOGIN;
  }, [logoutUserStore]);

  // Manual refresh
  const refreshSession = useCallback(async () => {
    try {
      setIsRefreshing(true);
      const validSession = await ensureValidToken();
      setSession(validSession);
      setIsAuthenticated(true);
      if (validSession.user) {
        saveUser(validSession.user);
      }
      return validSession;
    } catch (error) {
      console.error('Error refreshing session:', error);
      onLogout();
      throw error;
    } finally {
      setIsRefreshing(false);
    }
  }, [saveUser, onLogout]);

  const value = useMemo(
    () => ({
      session,
      isAuthenticated,
      isRefreshing,
      isInitializing,
      user: session?.user || null,
      onLogin,
      onLogout,
      refreshSession,
    }),
    [
      isAuthenticated,
      isInitializing,
      isRefreshing,
      onLogin,
      onLogout,
      refreshSession,
      session,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
